/**
 * Compact therapist selector for the calendar HeaderToolbar.
 *
 * Deliberately shadcn (dropdown-menu + button) instead of the previous MUI
 * outlined `Controls.Select`: the floating-label MUI select is ~56px tall and
 * dominated the toolbar, while this trigger is a 32px outline button matching
 * every other toolbar control. Built on the already-installed dropdown-menu
 * primitive (radix-select is not a dependency).
 */
import { ChevronsUpDown } from "lucide-react";
import { Button as ShadButton } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserDto } from "../../api-client";

interface TherapistSelectProps {
  users: UserDto[];
  /** Selected user id, or "" for all therapists. */
  value: string;
  onChange: (userId: string) => void;
}

const TherapistSelect = ({ users, value, onChange }: TherapistSelectProps) => {
  const selected = users.find((user) => String(user.id) === String(value));
  const label = selected
    ? `${selected.firstName} ${selected.lastName}`
    : "Alle Therapeuten";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ShadButton
          variant="outline"
          // Narrower than the menu it opens. Therapist names were already
          // truncating at the original w-52, so nothing changes in kind for
          // them; the binding constraint is the resting label "Alle
          // Therapeuten", which a control should not clip. w-36 fits it only
          // because the padding and chevron were tightened to match — shrink
          // this further and the trigger reads "Alle Therapeut…" at rest,
          // which looks broken rather than compact.
          className="h-8 w-36 justify-between px-1.5 font-normal"
        >
          <span className={selected ? "truncate" : "truncate opacity-60"}>{label}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </ShadButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 w-52 overflow-y-auto">
        <DropdownMenuRadioGroup value={String(value ?? "")} onValueChange={onChange}>
          <DropdownMenuRadioItem value="">Alle Therapeuten</DropdownMenuRadioItem>
          <DropdownMenuSeparator />
          {users.map((user) => (
            <DropdownMenuRadioItem key={user.id} value={String(user.id)}>
              {user.firstName} {user.lastName}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TherapistSelect;
